import { useEffect } from 'react';
import { blogSEOMeta } from '../../../mocks/blog-seo-meta';

interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  image: string;
  date: string;
  category: string;
  content: string;
}

const MESES: Record<string, number> = { jan: 1, fev: 2, mar: 3, abr: 4, mai: 5, jun: 6, jul: 7, ago: 8, set: 9, out: 10, nov: 11, dez: 12 };

/** Converte datas como '20 Maio 2026' para o formato ISO (2026-05-20) exigido pelo schema.org. */
function dataISO(s: string): string {
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const m = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().match(/(\d{1,2})\s*(?:de\s+)?([a-z]{3})[a-z]*\.?\s*(?:de\s+)?(\d{4})/);
  if (!m || !MESES[m[2]]) return s;
  return `${m[3]}-${String(MESES[m[2]]).padStart(2, '0')}-${m[1].padStart(2, '0')}`;
}

export function useBlogSEO(post: BlogPost | undefined) {
  useEffect(() => {
    if (!post) return;

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://stibpalestrasilenciosasp.com.br';

    const customMeta = blogSEOMeta[post.slug];
    const ogTitle = customMeta?.ogTitle ?? post.title;
    const seoDescription = customMeta?.description ?? post.excerpt;
    const seoKeywords = customMeta?.keywords
      ? customMeta.keywords.join(', ')
      : [
          'palestra silenciosa',
          'tradução simultânea',
          'eventos corporativos',
          'tecnologia de áudio',
          'São Paulo',
          post.category.toLowerCase(),
        ].join(', ');

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = post.content;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    const wordCount = textContent.split(/\s+/).filter(w => w.length > 0).length;

    const articleSchema = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      '@id': `${siteUrl}/blog/${post.slug}#article`,
      headline: ogTitle,
      description: seoDescription,
      image: {
        '@type': 'ImageObject',
        url: post.image.startsWith('http') ? post.image : `${siteUrl}${post.image}`,
        width: 1400,
        height: 781,
      },
      datePublished: dataISO(post.date),
      dateModified: dataISO(post.date),
      author: { '@type': 'Organization', name: 'Equipe STIB', url: siteUrl },
      publisher: {
        '@type': 'Organization',
        name: 'STIB',
        logo: {
          '@type': 'ImageObject',
          url: `${siteUrl}/img/stib/logo-stib-3d.png`,
          width: 200,
          height: 60,
        },
      },
      mainEntityOfPage: { '@type': 'WebPage', '@id': `${siteUrl}/blog/${post.slug}` },
      articleSection: post.category,
      articleBody: textContent.substring(0, 500),
      wordCount,
      inLanguage: 'pt-BR',
      isAccessibleForFree: true,
      keywords: seoKeywords,
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: `${siteUrl}/blog` },
        { '@type': 'ListItem', position: 3, name: post.title, item: `${siteUrl}/blog/${post.slug}` },
      ],
    };

    const ids = ['blog-article-schema', 'blog-breadcrumb-schema'];
    // Clean up existing injected schemas
    ids.forEach(id => {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
    });

    const schemas = [articleSchema, breadcrumbSchema];
    const scripts: HTMLScriptElement[] = [];

    schemas.forEach((schema, i) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.id = ids[i];
      s.text = JSON.stringify(schema);
      document.head.appendChild(s);
      scripts.push(s);
    });

    return () => {
      scripts.forEach(s => s.remove());
    };
  }, [post]);
}